import React, {
 useState, useEffect
} from 'react';
import {
 Card, CardContent, CardHeader, CardTitle, CardDescription
} from 'shared/components/ui/card';
import {
 Button
} from 'shared/components/ui/button';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
 useNavigate
} from 'react-router-dom';
import {
 Plug, Database, GitBranch, Check, Sparkles
} from 'lucide-react';
import {
 useDataBuilderV2
} from '../DataBuilderV2Context';

interface Step {
    id: string;
    step: number;
    title: string;
    description: string;
    icon: React.ElementType;
    href: string;
    actionLabel: string;
    requiresPrevious?: string;
}

const steps: Step[] = [
    {
        id: 'connect',
        step: 1,
        title: 'Create Your First Connection',
        description: 'Connect to your database, API, or file storage to get started.',
        icon: Plug,
        href: 'connections',
        actionLabel: 'Create Connection',
    },
    {
        id: 'schema',
        step: 2,
        title: 'Define Your First Schema',
        description: 'Model your data by creating tables, fields, and relationships. Or, use our AI to generate it for you.',
        icon: Database,
        href: 'connections',
        actionLabel: 'Define Schema',
        requiresPrevious: 'connect',
    },
    {
        id: 'workflow',
        step: 3,
        title: 'Create a Workflow',
        description: 'Automate a business process by creating a multi-step BeXAction Chain.',
        icon: GitBranch,
        href: 'workflows',
        actionLabel: 'Create Workflow',
    },
];

const OnboardingChecklist: React.FC = () => {
    const {
 appId, connectionCount, schemaCount
} = useDataBuilderV2();
    const navigate = useNavigate();
    const storageKey = `onboarding_skipped_${appId}`;
    const [skipped, setSkipped] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved === 'true') setSkipped(true);
    }, [storageKey]);

    const handleSkip = () => {
        localStorage.setItem(storageKey, 'true');
        setSkipped(true);
    };

    if (skipped) return null;

    const completedSteps: Record<string, boolean> = {
        connect: connectionCount > 0,
        schema: schemaCount > 0,
        workflow: false,
    };

    const completedCount = Object.values(completedSteps).filter(Boolean).length;
    const allCompleted = completedCount === steps.length;
    if (allCompleted) return null;

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-primary/10 p-2">
                            <Sparkles className="size-5 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">Get Started</CardTitle>
                            <CardDescription>Follow these steps to set up your application</CardDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="">{completedCount} / {steps.length} completed</Badge>
                        <Button variant="ghost" size="sm" onClick={handleSkip}>
                            Skip setup
                        </Button>
                    </div>
                </div>
                <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{
 width: `${(completedCount / steps.length) * 100}%`
}}
                    />
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                    {steps.map((step) => {
                        const isCompleted = completedSteps[step.id];
                        const isLocked = step.requiresPrevious && !completedSteps[step.requiresPrevious];

                        return (
                            <Card
                                key={step.id}
                                className={
                                    isCompleted
                                        ? 'border-green-500/50 bg-green-50/50 dark:bg-green-950/20'
                                        : isLocked
                                            ? 'opacity-60'
                                            : 'border-primary/20'
                                }
                            >
                                <CardContent className="p-5 flex flex-col gap-4 h-full">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            {isCompleted ? (
                                                <div className="rounded-full bg-green-500 p-2 shrink-0">
                                                    <Check className="size-5 text-white" />
                                                </div>
                                            ) : (
                                                <div className="rounded-full bg-muted p-2 shrink-0">
                                                    <step.icon className="size-5" />
                                                </div>
                                            )}
                                            <Badge variant="outline" className="text-xs">
                                                Step {step.step}
                                            </Badge>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2 flex-1">
                                        <h3 className="font-semibold text-base">{step.title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            {step.description}
                                        </p>
                                    </div>

                                    {isCompleted ? (
                                        <Badge variant="default" className="w-fit bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/10">
                                            <Check className="size-3 mr-1" /> Completed
                                        </Badge>
                                    ) : isLocked ? (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div>
                                                        <Button variant="outline" size="sm" className="w-fit" disabled>
                                                            {step.actionLabel}
                                                        </Button>
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Create a connection first</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    ) : (
                                        <Button
                                            size="sm"
                                            className="w-fit"
                                            onClick={() => navigate(step.href)}
                                        >
                                            {step.actionLabel}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};

export default OnboardingChecklist;
